import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { submitContactForm } from "../lib/api";

const TOPICS = [
  "General",
  "Pricing / Billing",
  "Account help",
  "Bug report",
  "Feature request",
  "Privacy / Data request",
];

const fieldClass =
  "bg-white border border-[#4a6984]/20 text-[#1c2d3d] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("General");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend = name.trim() && /\S+@\S+\.\S+/.test(email) && message.trim().length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend || busy) return;
    setBusy(true);
    try {
      await submitContactForm({ name: name.trim(), email: email.trim(), message: message.trim(), topic });
      setSent(true);
      toast.success("Message sent — we usually reply within a couple of school days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please email info@atomicedu.ca directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-[#f4f5f7] min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70 mb-2">
            Atomic Edu
          </p>
          <h1 className="text-3xl sm:text-4xl font-black text-[#1c2d3d] tracking-tight">
            Get in touch
          </h1>
          <p className="text-[#4a6984] font-medium mt-3 text-base sm:text-lg">
            Questions, ideas, bug reports — anything. We read every message.
          </p>
          <p className="text-[#4a6984]/80 text-sm mt-2 inline-flex items-center gap-2">
            <Mail className="h-4 w-4" /> Or email
            <a className="ae-link" href="mailto:info@atomicedu.ca">info@atomicedu.ca</a>
            directly.
          </p>
        </header>

        {sent ? (
          <div
            className="ae-card p-8 text-center space-y-3"
            data-testid="contact-success"
          >
            <h2 className="font-extrabold text-xl text-[#1c2d3d]">Thanks — message received.</h2>
            <p className="text-[#4a6984] font-medium">
              We'll reply to <strong>{email}</strong> shortly. You can close this tab.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="ae-card p-6 sm:p-8 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name" className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
                  Your name *
                </Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ms. Patel"
                  className={`${fieldClass} mt-1 h-11`}
                  maxLength={100}
                />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
                  Reply-to email *
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.ca"
                  className={`${fieldClass} mt-1 h-11`}
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
                Topic
              </Label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger data-testid="contact-topic" className={`${fieldClass} mt-1 h-11`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOPICS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="contact-message" className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70">
                Message *
              </Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                placeholder="Tell us what's on your mind — at least a couple of sentences so we can help properly."
                className={`${fieldClass} mt-1 resize-none`}
                maxLength={4000}
              />
              <p className="text-[10px] text-[#4a6984]/60 mt-1 font-medium">
                {message.trim().length < 10 ? `${10 - message.trim().length} more chars to send` : `${message.length} / 4000`}
              </p>
            </div>
            <div className="flex justify-end pt-2 border-t border-[#4a6984]/10">
              <Button
                type="submit"
                disabled={!canSend || busy}
                data-testid="contact-submit"
                className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white"
              >
                {busy ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send message</>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
